import type { Goal } from '../context/GoalsContext';
import { colors } from '../theme/colors';

export type GoalProgressStatus = 'Complete' | 'Ahead' | 'On Track' | 'Behind';

export const GOAL_PROGRESS_STATUSES: GoalProgressStatus[] = [
  'Complete',
  'Ahead',
  'On Track',
  'Behind',
];

const calculateTimeProgress = (deadline: string): number => {
  const now = new Date();
  const end = new Date(deadline);
  const start = new Date('2024-01-01');
  const totalDuration = end.getTime() - start.getTime();
  if (totalDuration <= 0) return 0;
  const elapsed = now.getTime() - start.getTime();
  return (elapsed / totalDuration) * 100;
};

/** Computes the progress status label shown on Progress goal cards. */
export function getGoalProgressStatus(goal: Goal): GoalProgressStatus {
  if (goal.currentAmount >= goal.targetAmount) {
    return 'Complete';
  }

  const timeProgress = calculateTimeProgress(goal.deadline);
  const amountProgress = (goal.currentAmount / goal.targetAmount) * 100;
  const difference = amountProgress - timeProgress;

  if (difference >= 10) {
    return 'Ahead';
  }
  if (difference >= -10) {
    return 'On Track';
  }
  return 'Behind';
}

/** Fields used for free-text search across goal cards. */
export function getGoalSearchableText(goal: Goal): string {
  const status = getGoalProgressStatus(goal);
  return [goal.name, goal.type, goal.priority, status, ...status.split(' ')]
    .join(' ')
    .toLowerCase();
}

export function goalMatchesSearchQuery(goal: Goal, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return getGoalSearchableText(goal).includes(q);
}

export const statusFilterColors: Record<GoalProgressStatus, string> = {
  Complete: colors.success.main,
  Ahead: colors.success.main,
  'On Track': colors.warning.main,
  Behind: colors.error.main,
};

export function getGoalProgressStatusInfo(goal: Goal): {
  status: GoalProgressStatus;
  color: string;
  message: string;
} {
  const status = getGoalProgressStatus(goal);

  switch (status) {
    case 'Complete':
      return {
        status,
        color: colors.success.main,
        message: "Congratulations! You've met your goal! 🎉",
      };
    case 'Ahead':
      return {
        status,
        color: colors.success.main,
        message: "You're ahead of schedule! Keep up the great work!",
      };
    case 'On Track':
      return {
        status,
        color: colors.warning.main,
        message: "You're on track to meet your goal.",
      };
    case 'Behind':
      return {
        status,
        color: colors.error.main,
        message:
          "You're falling behind. Consider increasing your monthly contribution.",
      };
  }
}
